
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface CreateApiKeyDialogProps {
  onApiKeyCreated: () => void;
}

const CreateApiKeyDialog = ({ onApiKeyCreated }: CreateApiKeyDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newRateLimit, setNewRateLimit] = useState(100);

  const generateApiKey = () => {
    // Generate cryptographically secure API key with more entropy
    const prefix = 'sk_';
    const randomBytes = crypto.getRandomValues(new Uint8Array(48)); // Increased from 32 to 48 bytes
    const randomString = Array.from(randomBytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    // Add timestamp entropy for additional uniqueness
    const timestamp = Date.now().toString(36);
    
    return prefix + randomString + timestamp;
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Key name is required');
      return;
    }

    try {
      const apiKey = generateApiKey();
      
      const { error } = await supabase
        .from('api_keys')
        .insert({
          key_name: newKeyName,
          api_key: apiKey,
          rate_limit_per_hour: newRateLimit
        });

      if (error) {
        toast.error('Failed to create API key');
        return;
      }

      toast.success('API key created successfully');
      setIsOpen(false);
      setNewKeyName('');
      setNewRateLimit(100);
      onApiKeyCreated();
    } catch (error) {
      toast.error('Error creating API key');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4 mr-2" />
          Create API Key
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New API Key</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="keyName">Key Name</Label>
            <Input
              id="keyName"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="Enter a descriptive name"
            />
          </div>
          <div>
            <Label htmlFor="rateLimit">Rate Limit (requests per hour)</Label>
            <Input
              id="rateLimit"
              type="number"
              value={newRateLimit}
              onChange={(e) => setNewRateLimit(parseInt(e.target.value) || 100)}
              min="1"
              max="10000"
            />
          </div>
          <Button onClick={createApiKey} className="w-full">
            Create API Key
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateApiKeyDialog;
